import React from 'react'
import { useSelector } from 'react-redux'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Badge } from 'reactstrap'

const formatDate = (d) => {
  if (!d) return '-'
  return new Date(d).toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

const formatMonth = (d) => {
  if (!d) return '-'
  return new Date(d).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })
}

const NotificationHistoryModal = ({ isOpen, toggle }) => {
  // Coalesced server-side: one entry per email, with arrays for the windows
  // + months it covered. Mirrors the queue's coalesce-by-day shape so a
  // single coalesced send shows as ONE row in both views, not N.
  const notifications = useSelector((state) => state.cspRenewals.notifications)

  // Newest first - sort by EmailSentAt with CreatedAt as a fallback so
  // legacy rows with null EmailSentAt still order sensibly.
  const sorted = React.useMemo(() => {
    return [...(notifications || [])].sort((a, b) => {
      const aKey = a.emailSentAt ?? a.createdAt
      const bKey = b.emailSentAt ?? b.createdAt
      return new Date(bKey) - new Date(aKey)
    })
  }, [notifications])

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>Notification History</ModalHeader>
      <ModalBody>
        {sorted.length === 0 ? (
          <p className="text-muted">No notifications have been sent yet.</p>
        ) : (
          <Table size="sm" hover bordered>
            <thead>
              <tr>
                <th>Sent</th>
                <th>Company</th>
                <th>Renewal Months</th>
                <th>Types</th>
                <th>Subs</th>
                <th>Email To</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((n) => {
                // Stable React key: ids of the underlying rows, joined.
                // Falls back to a (company, sent) hash if SourceRowIds is
                // somehow empty (defensive).
                const key = (n.sourceRowIds || []).join('|')
                  || `${n.companyId}-${n.emailSentAt ?? n.createdAt}`
                const months = (n.renewalMonths || []).map(formatMonth).join(', ')
                const types = n.notificationTypes || []
                const isCoalesced = types.length > 1
                return (
                  <tr key={key}>
                    <td>
                      {n.emailSentAt
                        ? formatDate(n.emailSentAt)
                        : <Badge color="warning">Not sent</Badge>}
                    </td>
                    <td>{n.companyName || <span className="text-muted">{n.companyId}</span>}</td>
                    <td>{months || <span className="text-muted">—</span>}</td>
                    <td>
                      {types.map((t) => (
                        <Badge key={t} color="info" className="mr-1">{t}</Badge>
                      ))}
                      {isCoalesced && (
                        <Badge color="secondary" pill className="ml-1" title="One email covering multiple notification types">
                          Coalesced
                        </Badge>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{n.totalSubscriptionCount}</td>
                    <td>{n.emailSentTo || <span className="text-muted">—</span>}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  )
}

export default NotificationHistoryModal
